/**
 * [evotek P24] Per-tenant brand token theming for dashboard.
 * [evotek P29] Brand-tinted sidebar icons + per-account logo.
 * [evotek P30] Auto-derive engine + DB-backed brand_theme support.
 *
 * Mechanism: patch CSS custom properties at source rather than overriding
 * compiled Tailwind classes.
 *
 * `theme/colors.js` defines:
 *   brand: 'rgb(var(--brand-color, 39 129 246) / <alpha-value>)'
 *
 * Every Tailwind utility using the brand token (bg-n-brand, bg-n-brand/10,
 * checked:bg-n-brand, @apply bg-n-brand…) compiles to
 * `rgb(var(--brand-color, …) / alpha)`. Overriding `--brand-color` per
 * account scope rethemes all of them simultaneously.
 *
 * ── P30: two-source brand config ─────────────────────────────────────────────
 *
 * Priority order (highest first):
 *   1. DB value: account.custom_attributes.brand_theme = { brandColor, logoUrl }
 *      → passed in as `brandThemeObj` parameter to setBrandTheme()
 *      → tokens derived on the fly via deriveBrandTheme()
 *   2. TENANT_BRAND_MAP legacy: hand-tuned map below (kept for backward compat
 *      until all accounts have brand_theme set in DB).
 *      DEPRECATED: will be removed once seed is applied and verified per account.
 *   3. No config → clear override, fallback to default iris/blue.
 *
 * ── P30: feature flag ─────────────────────────────────────────────────────────
 *
 * [P30 fix] brand_customization flag moved from FlagShihTzu features.yml (bigint
 * overflow at feature #64) to custom_attributes JSONB.
 * setBrandTheme checks account.custom_attributes.brand_customization_enabled before
 * applying DB-sourced theme. TENANT_BRAND_MAP legacy path bypasses the flag (so
 * existing prod behavior is unchanged until flag is enabled per account by ops).
 * Ops enable: acc.custom_attributes['brand_customization_enabled'] = true; acc.save!
 *
 * ── WCAG AA contrast (verified 2026-06-12) ────────────────────────────────────
 *
 * Hand-tuned values for TENANT_BRAND_MAP (unchanged from P29 rev2):
 *   MyTel button:      #1c2024 on #F58220  → 6.32:1  PASS AA
 *   MyTel link light:  #AA5500 on #FCFCFD  → 5.11:1  PASS AA
 *   MyTel link dark:   #FFA040 on #0A111C  → 9.33:1  PASS AA
 *   ViettelIDC button: #ffffff on #EE0033  → 4.48:1  NOTE: accepted as-is per customer
 *   ViettelIDC link light: #B40028 on #FCFCFD → 6.89:1  PASS AA
 *   ViettelIDC link dark:  #FF6B80 on #0A111C → 6.91:1  PASS AA
 *
 * [P29 rev2] Icon idle contrast (sidebar bg: light #FCFCFD / dark #1C1D20):
 *   MyTel idle light:   #AA5500 on #FCFCFD → 4.84:1  PASS
 *   MyTel idle dark:    rgb(245 130 32/0.65)≈#A95F20 on #1C1D20 → 3.69:1  PASS
 *   ViettelIDC idle light: rgb(238 0 51/0.65)≈#F25879 on #FCFCFD → 3.23:1  PASS
 *   ViettelIDC idle dark:  rgb(238 0 51/0.85)≈#CE0430 on #1C1D20 → 3.10:1  PASS
 *
 * Account IDs (server instance 172.31.2.23):
 *   acc 2 — Evotek demo / internal    → default iris/blue (no override)
 *   acc 3 — MyTel Myanmar             → orange #F58220
 *   acc 4 — Viettel IDC               → red #EE0033
 *
 * Build-dir note (see PATCHES.md P24):
 *   theme/colors.js MUST be included in the overlay copy list when building
 *   image p30+. File is in the build source root — verify it is not drifted
 *   from git before building: `diff ~/evochat-pN-src/theme/colors.js evochat/theme/colors.js`
 *
 * [P29] Brand-tinted sidebar icons + per-account logo:
 *   sidebarBrandIcons — inject brand-colored icon CSS with per-mode contrast variants.
 *   logoUrl           — URL of customer logo served from /customer-logos/ (nginx static).
 *                       Replaces the sidebar <img class="size-8"> via CSS content:url().
 *                       Logo files live in /var/www/ai-agent/customer-logos/ on the server.
 *                       CSS content:url() on <img>: Chrome 51+ / Firefox 63+.
 */

import { deriveBrandTheme } from 'dashboard/helper/brandColorDerive';

const STYLE_TAG_ID = 'evotek-brand-theme';

/**
 * Brand config per account (hand-tuned legacy map).
 * DEPRECATED in favor of DB brand_theme — kept for backward compat until seed applied.
 *
 * Fields (all values are CSS-valid strings):
 *   brandColorRGB      — "R G B" space-separated, used as value of --brand-color
 *   blue11Light        — "R G B" space-separated for --blue-11 in light mode
 *   blue11Dark         — "R G B" space-separated for --blue-11 in dark mode
 *   buttonTextRule     — optional CSS rule appended to scope block (dark text override)
 *   sidebarBrandIcons  — boolean; if true tint all sidebar icons with brand color
 *   iconIdleLight      — CSS color for idle icons in light mode (overrides brand/0.65)
 *   iconIdleDarkOpacity— opacity (0–1) for idle icons in dark mode (applied to brand color)
 *   logoUrl            — string URL or null; if set replace sidebar logo via CSS
 */
const TENANT_BRAND_MAP = {
  // Account 3 — MyTel Myanmar
  // Brand: orange #F58220 = rgb(245 130 32)
  // Button text: dark #1c2024 — white on orange only 2.53:1 FAIL; dark 6.32:1 PASS
  // Link light: #AA5500 = rgb(170 85 0) — 5.11:1 PASS AA vs #FCFCFD
  // Link dark:  #FFA040 = rgb(255 160 64) — 9.33:1 PASS AA vs #0A111C
  // Icon idle light: #AA5500 — 4.84:1 vs #FCFCFD (brand/1.0 = 2.55:1 FAIL)
  // Icon idle dark:  brand/0.65 → #A95F20 — 3.69:1 vs #1C1D20
  3: {
    brandColorRGB: '245 130 32',
    blue11Light: '170 85 0',
    blue11Dark: '255 160 64',
    buttonTextRule: '.bg-n-brand { color: #1c2024; }',
    sidebarBrandIcons: true,
    iconIdleLight: 'rgb(170 85 0)',      // #AA5500 — 4.84:1 vs #FCFCFD
    iconIdleDarkOpacity: 0.65,           // brand/0.65 → #A95F20 — 3.69:1 vs #1C1D20
    logoUrl: '/customer-logos/mytel.png?v=p35',
  },

  // Account 4 — Viettel IDC
  // Brand: red #EE0033 = rgb(238 0 51)
  // Button text: white — default, no override needed
  // Link light: #B40028 = rgb(180 0 40) — 6.89:1 PASS AA vs #FCFCFD
  // Link dark:  #FF6B80 = rgb(255 107 128) — 6.91:1 PASS AA vs #0A111C
  // Icon idle light: brand/0.65 → #F25879 — 3.23:1 vs #FCFCFD
  // Icon idle dark:  brand/0.85 → #CE0430 — 3.10:1 vs #1C1D20
  // logoUrl: null — TODO: add Viettel IDC logo file then set here
  4: {
    brandColorRGB: '238 0 51',
    blue11Light: '180 0 40',
    blue11Dark: '255 107 128',
    buttonTextRule: null,
    sidebarBrandIcons: true,
    iconIdleLight: null,                 // null → use brand/0.65 (3.23:1 PASS vs #FCFCFD)
    iconIdleDarkOpacity: 0.85,           // brand/0.85 → #CE0430 — 3.10:1 vs #1C1D20
    logoUrl: null,
  },
};

/**
 * [B1 Layer 3 / P31 B3] Safety-net guard for logoUrl before CSS content:url() interpolation.
 * Reject any value containing characters that could break CSS content:url() or allow injection.
 *
 * [P31 B3] Extended to allow two valid path prefixes:
 *   1. /customer-logos/  — legacy nginx static files (P29)
 *   2. /rails/active_storage/  — ActiveStorage blobs uploaded via P31 B3 upload endpoint
 *
 * IMPORTANT: For ActiveStorage URLs the URL is used in CSS content:url() (for legacy Logo.vue
 * CSS-override path) OR in <img :src> binding (for forked Logo.vue P31 approach).
 * When used in CSS, unsafe chars (quotes, parens, backslash, whitespace) must be rejected.
 * The :src binding approach is safer (no CSS injection risk) but the guard still applies
 * as a defence-in-depth against XSS via unintended path injection.
 *
 * @param {string|null} url
 * @returns {string|null}  sanitized url or null if rejected
 */
// [evotek P30 B2] [P31 B3 updated] exported for unit tests
export function sanitizeLogoUrl(url) {
  if (!url) return null;
  // Must start with one of the two allowed server-controlled path prefixes
  const isLegacyPath = url.startsWith('/customer-logos/');
  const isActiveStoragePath = url.startsWith('/rails/active_storage/');
  if (!isLegacyPath && !isActiveStoragePath) return null;
  // Reject path traversal attempts (e.g. /customer-logos/../etc/passwd)
  if (url.includes('..')) return null;
  // Reject characters that could break CSS: quotes, parens, backslash
  // Note: whitespace is still rejected (valid AS paths never contain spaces)
  if (/['"\\\s()]/.test(url)) return null;
  return url;
}

/**
 * Build the CSS <style> tag content for a given account.
 * Only injects CSS custom property definitions — no overrides of compiled
 * Tailwind classes, no !important except the active pill background where
 * upstream sidebar active uses a plain class with specificity (0,1,0) and
 * the scope selector (0,2,2) — `body.account-N aside .active` — wins without
 * !important. The !important is kept only as a safety net against Tailwind JIT
 * @apply rules that may emit at the same specificity in some build configs.
 *
 * @param {number} accountId
 * @param {Object} brand  — object with fields from TENANT_BRAND_MAP or deriveBrandTheme()
 * @returns {string} CSS text
 */
function buildBrandCSS(accountId, brand) {
  const scope = `body.account-${accountId}`;
  const scopeDark = `body.account-${accountId}.dark`;

  const lines = [
    `/* [evotek P24] Brand token override for account ${accountId} */`,

    // Light mode: set --brand-color + --blue-11 for link/accent text
    `${scope} {`,
    `  --brand-color: ${brand.brandColorRGB};`,
    `  --blue-11: ${brand.blue11Light};`,
    `}`,

    // Dark mode: flip --blue-11 to a lighter accessible value
    `${scopeDark} {`,
    `  --blue-11: ${brand.blue11Dark};`,
    `}`,
  ];

  // Optional button text color override (only for light-brand tenants)
  if (brand.buttonTextRule) {
    lines.push(`${scope} ${brand.buttonTextRule}`);
  }

  // [evotek P29] Brand-tinted sidebar icons
  // Icons use UnoCSS CSS-mask spans (i-lucide-*) — color cascades via currentColor.
  // Also covers inline SVG icons (ChannelIcon) with aside svg selector.
  // Active classes: .active (SidebarGroupLeaf), router-link-exact-active (router-link).
  //
  // Icon selector: span[class^="i-"], span[class*=" i-"] — matches both
  //   "i-lucide-home" (class starts with "i-") and
  //   "size-4 i-lucide-home" (class contains " i-" with leading space).
  // The substring-only pattern span[class*="i-"] was a P29 initial bug: it matched
  // any class containing "i-" (e.g. "min-h-[1rem]"). Fixed in rev2.
  //
  // Alert icon exclusion: ChannelLeaf.vue:37 renders
  //   <Icon icon="i-woot-alert" class="size-3 text-n-ruby-9" />
  // The ruby-9 color signals reauthorization-required error state — must not be tinted.
  // Exclusion: :not(.text-n-ruby-9) on the idle/active span selectors.
  if (brand.sidebarBrandIcons) {
    // Idle icon color: per-mode variants for WCAG contrast (see file header).
    const idleLightColor = brand.iconIdleLight
      ? brand.iconIdleLight
      : `rgb(var(--brand-color) / 0.65)`;
    // [M3] iconIdleDark takes precedence over opacity-based approach for dark brand
    // colors (e.g. #000000) where opacity cannot achieve 3:1 on the dark sidebar bg.
    const idleDarkColor = brand.iconIdleDark
      ? brand.iconIdleDark
      : `rgb(var(--brand-color) / ${brand.iconIdleDarkOpacity || 0.65})`;

    lines.push(
      `/* [evotek P29 rev2] Brand-tinted sidebar icons — per-mode contrast variants */`,

      // Idle light mode (default scope, no .dark)
      `${scope}:not(.dark) aside span[class^="i-"]:not(.text-n-ruby-9),`,
      `${scope}:not(.dark) aside span[class*=" i-"]:not(.text-n-ruby-9) { color: ${idleLightColor}; }`,
      `${scope}:not(.dark) aside svg { color: ${idleLightColor}; }`,

      // Idle dark mode
      `${scopeDark} aside span[class^="i-"]:not(.text-n-ruby-9),`,
      `${scopeDark} aside span[class*=" i-"]:not(.text-n-ruby-9) { color: ${idleDarkColor}; }`,
      `${scopeDark} aside svg { color: ${idleDarkColor}; }`,

      // Active: full brand color on icons (both modes)
      `${scope} aside .active span[class^="i-"]:not(.text-n-ruby-9),`,
      `${scope} aside .active span[class*=" i-"]:not(.text-n-ruby-9),`,
      `${scope} aside a.router-link-exact-active span[class^="i-"]:not(.text-n-ruby-9),`,
      `${scope} aside a.router-link-exact-active span[class*=" i-"]:not(.text-n-ruby-9),`,
      `${scope} aside .router-link-exact-active span[class^="i-"]:not(.text-n-ruby-9),`,
      `${scope} aside .router-link-exact-active span[class*=" i-"]:not(.text-n-ruby-9) { color: rgb(var(--brand-color)); }`,
      `${scope} aside .active svg,`,
      `${scope} aside a.router-link-exact-active svg,`,
      `${scope} aside .router-link-exact-active svg { color: rgb(var(--brand-color)); }`,

      // Active background pill (brand/10)
      `${scope} aside .active,`,
      `${scope} aside a.router-link-exact-active,`,
      `${scope} aside .router-link-exact-active { background-color: rgb(var(--brand-color) / 0.1) !important; }`,

      // Hover (not active)
      `${scope} aside a:hover:not(.active):not(.router-link-exact-active),`,
      `${scope} aside button:hover { background-color: rgb(var(--brand-color) / 0.06); }`,
    );
  }

  // [evotek P29] Per-account sidebar logo via CSS content:url()
  // Replaces sidebar <img class="size-8"> (Logo.vue) without touching Vue source.
  // Logo files served from /customer-logos/ (nginx static, /var/www/ai-agent/customer-logos/).
  // Prerequisite: LOGO_THUMBNAIL installation config must be set so Logo.vue renders
  // <img class="size-8"> rather than its SVG fallback.
  // [B1 Layer 3] sanitizeLogoUrl guards against injection in CSS interpolation.
  const safeLogoUrl = sanitizeLogoUrl(brand.logoUrl);
  if (safeLogoUrl) {
    lines.push(
      `/* [evotek P29] Customer logo */`,
      `${scope} aside img.size-8 { content: url('${safeLogoUrl}'); }`,
    );
  }

  return lines.join('\n');
}

/**
 * [B2] Check whether the brand_customization feature is enabled.
 *
 * [P30 fix] Moved from FlagShihTzu features (bigint overflow at feature #64) to
 * custom_attributes JSONB. The flag is now read from:
 *   account.custom_attributes.brand_customization_enabled === true
 * exposed by _account.json.jbuilder as custom_attributes.brand_customization_enabled.
 *
 * The caller passes customAttributes (account.custom_attributes object from API).
 * Backward compat: if passed a features Hash/Array (old callers before P30 fix),
 * we fall back gracefully to false — the flag no longer lives in features.
 *
 * @param {Object|null|undefined} customAttributes  — account.custom_attributes from API
 * @returns {boolean}
 */
function isBrandCustomizationEnabled(customAttributes) {
  if (!customAttributes) return false;
  if (typeof customAttributes !== 'object' || Array.isArray(customAttributes)) return false;
  return customAttributes.brand_customization_enabled === true;
}

/**
 * [B2 Layer 2] Validate brandColor and logoUrl from DB before applying to DOM.
 * Client-side guard — prevents bad values from Super Admin (or any source) from
 * reaching CSS interpolation even if server validation was bypassed.
 *
 * - brandColor: must match /^#[0-9a-fA-F]{6}$/
 * - logoUrl: must be null/empty OR start with /customer-logos/ AND NOT contain
 *   quotes, parens, backslash, or whitespace that could break CSS.
 *
 * On fail: log warning and return false → resolveBrandConfig falls to next source.
 *
 * @param {Object} brandThemeObj
 * @returns {boolean}
 */
// [evotek P30 B2] exported for unit tests
export function isBrandThemeObjValid(brandThemeObj) {
  if (!brandThemeObj || typeof brandThemeObj !== 'object') return false;
  const { brandColor, logoUrl } = brandThemeObj;
  // brandColor: mandatory, strict hex
  if (!brandColor || !/^#[0-9a-fA-F]{6}$/.test(brandColor)) {
    // eslint-disable-next-line no-console
    console.warn('[evochat brandTheme] resolveBrandConfig: invalid brandColor rejected', brandColor);
    return false;
  }
  // logoUrl: optional — blank is OK; if set must pass path + char guards.
  // [P31 B3] Also allow /rails/active_storage/ prefix for uploaded logos.
  if (logoUrl) {
    const isLegacyPath = logoUrl.startsWith('/customer-logos/');
    const isActiveStoragePath = logoUrl.startsWith('/rails/active_storage/');
    if (!isLegacyPath && !isActiveStoragePath) {
      // eslint-disable-next-line no-console
      console.warn('[evochat brandTheme] resolveBrandConfig: logoUrl rejected (wrong path prefix)', logoUrl);
      return false;
    }
    // [evotek P30 fix] Reject path traversal attempts
    if (logoUrl.includes('..')) {
      // eslint-disable-next-line no-console
      console.warn('[evochat brandTheme] resolveBrandConfig: logoUrl rejected (path traversal)', logoUrl);
      return false;
    }
    if (/['"\\\s()]/.test(logoUrl)) {
      // eslint-disable-next-line no-console
      console.warn('[evochat brandTheme] resolveBrandConfig: logoUrl rejected (unsafe chars)', logoUrl);
      return false;
    }
  }
  return true;
}

/**
 * Resolve brand config from the priority chain:
 *
 *   1. DB brandThemeObj (from account.custom_attributes.brand_theme) +
 *      flag check (custom_attributes.brand_customization_enabled) →
 *      derive tokens via deriveBrandTheme()
 *      [P31 B3] Logo priority within DB path:
 *        a. brandLogoUrl (ActiveStorage, from account.brand_logo_url) — takes precedence
 *        b. brandThemeObj.logoUrl (manual URL under /customer-logos/) — fallback
 *   2. TENANT_BRAND_MAP legacy hard-coded (deprecated, for backward compat)
 *   3. null → no override
 *
 * @param {number} accountId
 * @param {Object|null} brandThemeObj  — { brandColor, logoUrl } from DB, or null/undefined
 * @param {Object|null} customAttributes — account.custom_attributes from API
 * @param {string|null} brandLogoUrl   — account.brand_logo_url from API (ActiveStorage)
 * @returns {Object|null}  brand config ready for buildBrandCSS, or null
 */
function resolveBrandConfig(accountId, brandThemeObj, customAttributes, brandLogoUrl) {
  // Source 1: DB brand_theme with flag check
  // [P30 fix] isBrandCustomizationEnabled reads custom_attributes.brand_customization_enabled.
  // [B2 Layer 2] isBrandThemeObjValid validates brandColor and logoUrl before derive.
  if (
    brandThemeObj &&
    isBrandCustomizationEnabled(customAttributes) &&
    isBrandThemeObjValid(brandThemeObj)
  ) {
    const derived = deriveBrandTheme(brandThemeObj.brandColor);
    // [P31 B3] Logo priority:
    //   - brandLogoUrl (ActiveStorage upload) takes precedence over manual logoUrl.
    //   - Both are validated by sanitizeLogoUrl (now accepts /rails/active_storage/ too).
    //   - If neither is valid/present, logoUrl is null (no logo CSS rule emitted).
    const resolvedLogoUrl =
      sanitizeLogoUrl(brandLogoUrl) ||
      sanitizeLogoUrl(brandThemeObj.logoUrl) ||
      null;
    return {
      ...derived,
      logoUrl: resolvedLogoUrl,
    };
  }

  // Source 2: TENANT_BRAND_MAP legacy (deprecated — use while DB not seeded)
  if (Object.hasOwn(TENANT_BRAND_MAP, accountId)) {
    return TENANT_BRAND_MAP[accountId];
  }

  // Source 3: no config
  return null;
}

/**
 * Set brand theme for a given account.
 *
 * Call signature (P31 B3 updated):
 *   setBrandTheme(accountId, brandThemeObj | null, customAttributes | {}, brandLogoUrl | null)
 *
 * Backward-compat (P24/P29/P30 call sites continue to work):
 *   setBrandTheme(accountId)        → falls back to TENANT_BRAND_MAP
 *   setBrandTheme(null)             → clears override
 *
 * [P31 B3] Logo priority (within DB path, when brand_customization_enabled):
 *   brandLogoUrl (ActiveStorage, arg 4) > brandThemeObj.logoUrl (manual URL, in arg 2) > null
 *
 * Idempotent: calling with the same arguments multiple times is safe.
 *
 * @param {number|null|undefined} accountId
 * @param {Object|null|undefined} brandThemeObj     { brandColor: "#RRGGBB", logoUrl: "..." }
 * @param {Object|null|undefined} customAttributes  account.custom_attributes from API
 * @param {string|null|undefined} brandLogoUrl      account.brand_logo_url from API (ActiveStorage)
 */
export function setBrandTheme(accountId, brandThemeObj, customAttributes, brandLogoUrl) {
  // Remove old account-N classes from body
  Array.from(document.body.classList)
    .filter(cls => cls.startsWith('account-'))
    .forEach(cls => document.body.classList.remove(cls));

  // Remove old injected style tag
  const existing = document.getElementById(STYLE_TAG_ID);
  if (existing) {
    existing.remove();
  }

  if (!accountId) {
    // No account context — body class cleared above, done.
    return;
  }

  const brand = resolveBrandConfig(
    accountId,
    brandThemeObj,
    customAttributes || {},
    brandLogoUrl || null
  );

  if (!brand) {
    // Unknown account (e.g. acc 2 Evotek default) — fallback to default iris/blue.
    return;
  }

  // Add account scope class to body
  document.body.classList.add(`account-${accountId}`);

  // Inject style tag into <head>
  const style = document.createElement('style');
  style.id = STYLE_TAG_ID;
  style.setAttribute('data-evotek', 'brand-theme');
  style.textContent = buildBrandCSS(accountId, brand);
  document.head.appendChild(style);
}
